import { useState } from 'react';

import {
  FormData,
  FormState,
  UseSDKMethodFormReturn,
} from '@/features/method-tester/types';
import { getValidKeyValuePairs } from '@/utils';

export const useSDKMethodForm = (
  initialData: FormData = { className: '', methodName: '', params: {} }
): UseSDKMethodFormReturn => {
  const [formState, setFormState] = useState<FormState>({
    data: initialData,
    paramKeys: [''],
  });

  // Update a specific form field
  const updateField = (field: keyof FormData, value: string) => {
    setFormState((prev) => ({
      ...prev,
      data: { ...prev.data, [field]: value },
    }));
  };

  // Update parameter key at specific index
  const updateParamKey = (index: number, newKey: string) => {
    setFormState((prev) => {
      const oldKey = prev.paramKeys[index];
      const newKeys = [...prev.paramKeys];
      newKeys[index] = newKey;

      // Update params object when key changes
      const newParams = { ...prev.data.params };
      if (oldKey !== newKey && oldKey in newParams) {
        const oldValue = newParams[oldKey];
        delete newParams[oldKey];
        if (newKey) {
          newParams[newKey] = oldValue;
        }
      }

      return {
        ...prev,
        paramKeys: newKeys,
        data: { ...prev.data, params: newParams },
      };
    });
  };

  // Update parameter value at specific index
  const updateParamValue = (index: number, value: string) => {
    setFormState((prev) => {
      const key = prev.paramKeys[index];
      if (!key) return prev;
      return {
        ...prev,
        data: {
          ...prev.data,
          params: { ...prev.data.params, [key]: value },
        },
      };
    });
  };

  // Add new parameter row
  const addParamRow = () => {
    setFormState((prev) => ({
      ...prev,
      paramKeys: [...prev.paramKeys, ''],
    }));
  };

  // Remove parameter at specific index
  const removeParam = (index: number) => {
    setFormState((prev) => {
      const keyToRemove = prev.paramKeys[index];
      const newKeys = prev.paramKeys.filter((_, i) => i !== index);
      const newParams = { ...prev.data.params };

      if (keyToRemove && keyToRemove in newParams) {
        delete newParams[keyToRemove];
      }

      return {
        ...prev,
        paramKeys: newKeys.length === 0 ? [''] : newKeys,
        data: { ...prev.data, params: newParams },
      };
    });
  };

  // Reset form to initial state
  const reset = () => {
    setFormState({
      data: initialData,
      paramKeys: [''],
    });
  };

  // Get prepared data for submission
  const getSubmissionData = () => {
    return {
      className: formState.data.className.trim(),
      methodName: formState.data.methodName.trim(),
      params: getValidKeyValuePairs(formState.data.params),
    };
  };

  return {
    formState,
    updateField,
    updateParamKey,
    updateParamValue,
    addParamRow,
    removeParam,
    reset,
    getSubmissionData,
  };
};
